import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * "Because you liked X" / "More like this" recommendations.
 * Strategy: pull the target product's category + brand, then surface other
 * in-stock products in the same category, falling back to trending if the
 * category is too thin. This avoids a live OpenAI call on every product
 * page (cost + latency) while still being genuinely related to the source
 * product rather than globally trending.
 */
export const fetchRelatedProducts = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        productId: z.string().uuid(),
        limit: z.number().int().min(1).max(24).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const limit = data.limit ?? 8;

    const { data: source, error: srcErr } = await supabaseAdmin
      .from("products")
      .select("id, category, brand")
      .eq("id", data.productId)
      .maybeSingle();
    if (srcErr || !source) return { products: [], strategy: "none" as const };

    // Prefer same category (newer rows first), excluding the source itself.
    const { data: sameCategory } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("category", source.category)
      .neq("id", source.id)
      .eq("in_stock", true)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (sameCategory && sameCategory.length >= limit) {
      return { products: sameCategory, strategy: "same_category" as const };
    }

    const { data: fallback } = await supabaseAdmin
      .from("trending_products")
      .select("*")
      .neq("id", source.id)
      .limit(limit);

    return {
      products: fallback ?? sameCategory ?? [],
      strategy: "trending_fallback" as const,
    };
  });

/**
 * Homepage / discover-feed "Recommended for you" based on a shopper's own
 * click history (categories they've engaged with). Anonymous users and
 * users with no history fall back to global trending.
 */
export const fetchPersonalizedRecommendations = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        userId: z.string().uuid().optional(),
        limit: z.number().int().min(1).max(24).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const limit = data.limit ?? 12;

    if (!data.userId) {
      const { data: trending } = await supabaseAdmin
        .from("trending_products")
        .select("*")
        .limit(limit);
      return { products: trending ?? [], strategy: "trending_anonymous" as const };
    }

    const { data: recentClicks } = await supabaseAdmin
      .from("clicks")
      .select("product:products(category)")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(50);

    const categoryCounts = new Map<string, number>();
    for (const row of recentClicks ?? []) {
      const cat = (row as { product?: { category?: string } | null })?.product?.category;
      if (cat) categoryCounts.set(cat, (categoryCounts.get(cat) ?? 0) + 1);
    }
    const topCategory = [...categoryCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];

    if (!topCategory) {
      const { data: trending } = await supabaseAdmin
        .from("trending_products")
        .select("*")
        .limit(limit);
      return { products: trending ?? [], strategy: "trending_no_history" as const };
    }

    const { data: personalized } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("category", topCategory)
      .eq("in_stock", true)
      .order("created_at", { ascending: false })
      .limit(limit);

    return { products: personalized ?? [], strategy: "category_affinity" as const };
  });